import { User as UserIcon, Calendar, Mail, Crop as CropIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ProfileHeaderProps {
  user: {
    id?: number;
    name?: string;
    email: string;
    company?: string;
    profileImageUrl?: string;
    createdAt?: string | Date;
    role?: string;
  };
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'logo') => void;
}

export default function ProfileHeader({ user, onImageUpload }: ProfileHeaderProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserIcon className="h-5 w-5" />
          Profile Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-24 h-24 bg-neutral-200 rounded-full flex items-center justify-center overflow-hidden">
              {user.profileImageUrl && !user.profileImageUrl.startsWith('blob:') ? (
                <img
                  src={user.profileImageUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Hide image on error, fallback to icon
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <UserIcon className="h-8 w-8 text-neutral-400" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 bg-[#0240FB] text-white p-1 rounded-full cursor-pointer hover:bg-[#01207E]" title="Upload and crop profile picture">
              <CropIcon className="h-3 w-3" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onImageUpload(e, 'profile')}
              />
            </label>
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-neutral-900">{user.name}</h3>
            <p className="text-sm text-neutral-600">{user.email}</p>
            {user.company && (
              <p className="text-sm text-neutral-600">{user.company}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {user.id && (
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <span className="font-mono font-medium">ID: {user.id}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Calendar className="h-4 w-4" />
            <span>
              Member since {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Mail className="h-4 w-4" />
            <span>Role: {user.role}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
